import {
  Children,
  PropsWithChildren,
  isValidElement,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import "./combobox.scss";
import { FormInputElement } from "@archetypes/form-element";
import { LabelInfo } from "@components/label-info";
import { FormError } from "@components/form-error";
import { Button } from "@components/button";
import { useDebounceValue } from "usehooks-ts";
import { matchSorter } from "match-sorter";
import { Action } from "@components/action/action";
import { isNullOrUndefined } from "@utils/data-type.util";
import { FIELD_EVENT_DEBOUNCE_TIME } from "@constants/form";
import { CloseIcon } from "@components/icons";

export interface ComboBoxOption<T> extends PropsWithChildren {
  value: T;
  className?: string;
  searchValue?: string;
  checked?: boolean;
  onClick?: (value: T) => void;
  disabled?: boolean;
}

export const COption = <T,>({
  value,
  className,
  children,
  onClick,
  checked,
  disabled = false,
}: ComboBoxOption<T>) => {
  return (
    <div className="w-100">
      <Button
        disabled={disabled}
        onClick={() => onClick?.(value)}
        variant={checked ? "primary" : "default"}
        className={className + " justify-start no-radius"}
      >
        {children}
      </Button>
    </div>
  );
};

interface ComboboxProps<T> extends FormInputElement {
  onChange?: (value?: T) => void;
  value?: T;
  getKey: (value: T) => string | number;
  renderValue: (value: T) => string | undefined;
  searchKeys?: string[];
  errorMessage?: string;
  containerClassName?: string;
  labelClassName?: string;
  allowDeselect?: boolean;
  infoText?: string;
  keepOpenOnClick?: boolean;
  zIndex?: string;
}

const ComboboxComponent = <T,>({
  value,
  onChange,
  placeholder = "Select items",
  getKey,
  renderValue,
  searchKeys = [],
  label,
  errorMessage,
  containerClassName,
  labelClassName,
  required,
  infoText,
  children,
  allowDeselect = false,
  disabled,
  keepOpenOnClick,
  zIndex,
}: ComboboxProps<T>) => {
  const arrayOfChildren = useMemo(() => {
    return Children.toArray(children);
  }, [children]);

  const valueKey = useMemo(() => (value ? getKey(value) : undefined), [getKey, value]);
  const [inputValue, setInputValue] = useState<string>("");
  const [selectedFromDropdown, setSelectedFromDropdown] = useState<boolean>(false);

  const [debouncedValue] = useDebounceValue<string>(inputValue, FIELD_EVENT_DEBOUNCE_TIME);

  useEffect(() => {
    if (value) {
      setInputValue(renderValue(value) ?? "");
      setSelectedFromDropdown(true);
    } else {
      setInputValue("");
      setSelectedFromDropdown(false);
    }
  }, [renderValue, value]);

  const onOptionClick = useCallback(
    (value?: T) => {
      onChange?.(value);
    },
    [onChange],
  );

  const hideOption = useCallback(
    (optionValue: T) => {
      if (
        !selectedFromDropdown &&
        !matchSorter(
          [optionValue],
          debouncedValue,
          Array.isArray(searchKeys) && searchKeys.length ? { keys: searchKeys } : undefined,
        ).length
      ) {
        return true;
      }
      return false;
    },
    [debouncedValue, searchKeys, selectedFromDropdown],
  );

  const removeItem = useCallback(() => {
    onChange?.(undefined);
  }, [onChange]);

  return (
    <div className={`select-wrapper ${containerClassName}`}>
      <LabelInfo
        label={label}
        labelClassName={labelClassName}
        required={required}
        infoText={infoText}
      />
      <div className="d-flex relative">
        <Action
          zIndex={zIndex}
          label={!isNullOrUndefined(value) ? <>{renderValue(value as T)}</> : placeholder}
          mode="select"
          disabled={disabled}
          keepOpenOnClick={keepOpenOnClick}
          containerClassName="mx-h-200"
        >
          {arrayOfChildren.map((child) => {
            if (isValidElement<ComboBoxOption<T>>(child)) {
              const optionValue = child.props.value as T;
              if (hideOption(optionValue)) {
                return null;
              }
              const key = getKey(optionValue);
              return (
                <COption
                  {...child.props}
                  key={key}
                  onClick={onOptionClick}
                  className="w-100"
                  checked={key === valueKey}
                >
                  {child.props.children}
                </COption>
              );
            }
            return null;
          })}
        </Action>
        {allowDeselect && !isNullOrUndefined(value) && (
          <Button
            variant="transparent"
            className="absolute t-0 r-50 p-1 m-0 overflow-visible"
            onClick={removeItem}
          >
            <CloseIcon color="#292D32" size={11} />
          </Button>
        )}
      </div>

      <FormError errorMessage={errorMessage} />
    </div>
  );
};

export const Combobox = memo(ComboboxComponent) as typeof ComboboxComponent;
